import 'moment/locale/fr';

import {
  reserve_status,
  reserves_types,
} from '../../enums/reserve-status.enum';

import { faClipboard } from '@fortawesome/free-regular-svg-icons';

import React, { useMemo, useState, useEffect, useRef } from 'react';

import { faFilter, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams } from 'react-router-dom';

import useGetRequest from '../../api/useGetRequest';
import { useSelector } from 'react-redux';
import EmptyList from '../../components/agv-empty-list';
import GenericPageContainer from '../../components/agv-generic-page-container';
import GenericTable from '../../components/agv-generic-table';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import SearchFilter from '../../components/agv-header-search';
import SelectFilter from '../../components/agv-header-select';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import InfiniteScroll from 'react-infinite-scroll-component';
import './reserves.css';
import { returnReserveSubtitle } from '../../utils/reserve.util';
import ModalReserveV2 from './ModalReserveV2';
import { returnCriticalityDiv } from '../../utils/criticalities';
import {
  CRITICALITIES,
  CRITICALITIES_DISPLAY_NAME,
} from '../../enums/criticalities-enum.enum';
import { buildUrlWithParams } from '../../utils/url.utils';
import { mergeItemsByDate } from '../../utils/items-grouped-by-date.util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function Reserves() {
  const [searchParams] = useSearchParams();

  const features = useSelector((state) => state.features);
  const userFactory = useSelector((state) => state.user_factory);

  const [modalHandleReserve, setModalHandleReserve] = useState(false);
  const [selectedReserve, setSelectedReserve] = useState(null);
  const hasAutoOpened = useRef(false);
  const initialFilters = {
    limit: 24,
    offset: 0,
    query: searchParams.get('query') || '',
    ...(searchParams.get('status') &&
      (searchParams.get('status') === reserve_status.CREATED ||
        searchParams.get('status') === reserve_status.SOLVED) && {
        status: searchParams.get('status'),
      }),
    ...(searchParams.get('criticalities') &&
      [
        CRITICALITIES.URGENT,
        CRITICALITIES.HIGH,
        CRITICALITIES.MEDIUM,
        CRITICALITIES.LOW,
      ].includes(searchParams.get('criticalities')) && {
        criticalities: searchParams.get('criticalities'),
      }),
    ...(searchParams.get('vehicle_keys') && {
      vehicle_keys: searchParams.get('vehicle_keys'),
    }),
    ...(userFactory && { factory_id: userFactory }),
  };

  const {
    items,
    hasMore,
    filters,
    fetchMoreData,
    handleUpdateParam,
    isLoading,
    mutate,
  } = useInfiniteScroll({
    initialFilters,
    debounceTime: 300,
    getRequest: useGetRequest,
    endpoint: '/reserves',
  });

  useGlobalFilters(handleUpdateParam);

  const mergedItems = useMemo(() => {
    return mergeItemsByDate(items);
  }, [items]);

  const reserveReasonsUrl = useMemo(
    () =>
      buildUrlWithParams('/reserve-reasons', {
        limit: 1000,
        offset: 0,
      }),
    []
  );

  const {
    data: reserveReasons,
    isLoading: isLoadingReserveReasons,
    mutate: mutateReserveReasons,
  } = useGetRequest(reserveReasonsUrl, 300);

  const { data: vehiclesData, isLoading: isLoadingVehicles } = useGetRequest(
    '/reserves/vehicles',
    300
  );

  const onOpenModal = (element) => {
    setSelectedReserve(element);
    setModalHandleReserve(true);
  };

  // Réinitialiser le flag quand les paramètres de recherche changent
  useEffect(() => {
    hasAutoOpened.current = false;
  }, [searchParams.get('query'), searchParams.get('status')]);

  useEffect(() => {
    const isRedirected = searchParams.get('redirected') === 'true';

    // Compter le nombre total de réserves dans mergedItems
    const totalReserves = mergedItems.reduce((total, dateSection) => {
      return total + (dateSection.items ? dateSection.items.length : 0);
    }, 0);

    if (
      isRedirected &&
      !isLoading &&
      totalReserves === 1 &&
      !modalHandleReserve &&
      !hasAutoOpened.current
    ) {
      // Trouver la première (et unique) réserve dans mergedItems
      const firstReserve = mergedItems.find(
        (dateSection) => dateSection.items && dateSection.items.length > 0
      )?.items[0];

      if (firstReserve) {
        // Marquer comme déjà ouvert pour éviter la boucle
        hasAutoOpened.current = true;

        // Ouvrir automatiquement la modale de la seule réserve trouvée
        onOpenModal(firstReserve);
      }
    }
  }, [mergedItems, isLoading, searchParams, modalHandleReserve]);

  const handleReserveFieldsShown = (reserve) => {
    const fields = [];

    fields.push(
      {
        name: 'Client',
        value: (
          <div className="tw-w-[200px]">
            {reserve.receiver_name ? reserve?.receiver_name : '-'}
          </div>
        ),
      },
      {
        name: 'Provenance',
        value: (
          <div className="tw-w-[130px]">
            {reserves_types?.properties[reserve?.process_linked]?.display_name}
          </div>
        ),
      }
    );

    if (reserve?.reserve_reason?.criticality) {
      fields.push({
        name: '',
        value: returnCriticalityDiv(reserve?.reserve_reason?.criticality),
      });
    }

    return fields;
  };

  return (
    <>
      <GenericPageContainer
        title={'Gestion des réserves'}
        subtitle={"Retrouvez l'historique des réserves et leurs détails."}
        filters={
          <>
            <SearchFilter
              value={filters.query}
              setValue={(value) => handleUpdateParam('query', value)}
              disabled={isLoading}
            />
            <SelectFilter
              value={filters.status}
              onChange={(value) => handleUpdateParam('status', value)}
              options={[
                {
                  value: '',
                  name: reserve_status?.properties?.all?.display_name,
                },
                {
                  value: reserve_status.CREATED,
                  name: reserve_status?.properties?.CREATED?.display_name,
                },
                {
                  value: reserve_status.SOLVED,
                  name: reserve_status?.properties?.SOLVED?.display_name,
                },
              ]}
              icon={faFilter}
            />
            <SelectFilter
              value={filters.type}
              onChange={(value) => handleUpdateParam('type', value)}
              options={[
                {
                  value: '',
                  name: reserves_types?.properties?.all?.display_name,
                },
                {
                  value: reserves_types.RESERVE,
                  name: reserves_types?.properties?.RESERVE?.display_name,
                },
                {
                  value: reserves_types.RESERVE_CLIENT,
                  name: reserves_types?.properties?.RESERVE_CLIENT
                    ?.display_name,
                },
                {
                  value: reserves_types.RESERVE_ITEM,
                  name: reserves_types?.properties?.RESERVE_ITEM?.display_name,
                },
                {
                  value: reserves_types['UNABLE-TO-DELIVER'],
                  name: reserves_types?.properties['UNABLE-TO-DELIVER']
                    ?.display_name,
                },
                {
                  value: reserves_types.CHECKLIST,
                  name: reserves_types?.properties?.CHECKLIST?.display_name,
                },
                {
                  value: reserves_types.TOUR,
                  name: reserves_types?.properties?.TOUR?.display_name,
                },
              ]}
              icon={faFilter}
            />
            <SelectFilter
              value={filters.vehicle_keys}
              onChange={(value) => handleUpdateParam('vehicle_keys', value)}
              options={[
                {
                  value: '',
                  name: 'Tous les véhicules',
                },
                ...(vehiclesData?.vehicles?.map((vehicle) => ({
                  value: vehicle.vehicule_key,
                  name: vehicle.label
                    ? `${vehicle.vehicule_key} - ${vehicle.label}`
                    : vehicle.vehicule_key,
                })) || []),
              ]}
              icon={faFilter}
              width={200}
            />
            {features?.TOUR_STEP?.STEP_ACTIONS?.cancel?.enabled &&
              features?.TOUR_STEP?.STEP_ACTIONS?.cancel?.reasonAttendance
                ?.enabled && (
                <>
                  <SelectFilter
                    value={filters.reserve_reason_ids}
                    onChange={(value) =>
                      handleUpdateParam('reserve_reason_ids', value)
                    }
                    options={[
                      {
                        value: '',
                        name: 'Toutes les catégories',
                      },
                      ...(reserveReasons?.items?.map((item) => ({
                        value: item.id,
                        name: item.reason_message,
                      })) || []),
                    ]}
                    icon={faFilter}
                    width={200}
                  />
                  <SelectFilter
                    value={filters.criticalities}
                    onChange={(value) =>
                      handleUpdateParam('criticalities', value)
                    }
                    options={[
                      {
                        value: '',
                        name: 'Criticité',
                      },
                      {
                        value: CRITICALITIES.URGENT,
                        name: CRITICALITIES_DISPLAY_NAME[CRITICALITIES.URGENT],
                      },
                      {
                        value: CRITICALITIES.HIGH,
                        name: CRITICALITIES_DISPLAY_NAME[CRITICALITIES.HIGH],
                      },
                      {
                        value: CRITICALITIES.MEDIUM,
                        name: CRITICALITIES_DISPLAY_NAME[CRITICALITIES.MEDIUM],
                      },
                      {
                        value: CRITICALITIES.LOW,
                        name: CRITICALITIES_DISPLAY_NAME[CRITICALITIES.LOW],
                      },
                    ]}
                    icon={faFilter}
                  />
                </>
              )}
          </>
        }
      >
        {isLoading && filters.offset === 0 ? (
          <GenericTableSkeleton numberOfRows={10} hasTitle />
        ) : mergedItems?.length > 0 ? (
          <InfiniteScroll
            dataLength={mergedItems.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<GenericTableSkeleton numberOfRows={3} hasTitle />}
            scrollableTarget="page-content"
            hasChildren={mergedItems.length > 0}
            endMessage={
              <p className="agrivitech-pagination-text tw-mb-2 tw-mt-8 tw-text-center">
                Vous avez vu toutes les données pour cette recherche
              </p>
            }
          >
            {mergedItems.map((dateSection) => (
              <GenericTable
                title={
                  new Date(dateSection.date).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  }) || "Pas d'information de date"
                }
                isLoading={isLoading}
                key={`date_${dateSection?.date}`}
                rows={dateSection.items.map((element) => ({
                  title: (
                    <div
                      style={{
                        textOverflow: 'ellipsis',
                        textWrap: 'wrap',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {element.message}
                    </div>
                  ),
                  subtitle: returnReserveSubtitle(element),
                  onClick: () => onOpenModal(element),
                  buttonText: 'Détails',
                  status:
                    element?.status == 'CREATED'
                      ? STATUS_ENUM.WARN
                      : STATUS_ENUM.POSITIVE,
                  icon: faClipboard,
                  statusText:
                    element?.status == 'CREATED' ? 'A traiter' : 'Résolue',
                  fields: handleReserveFieldsShown(element),
                }))}
                iconColor={'tw-text-checklist-common'}
              />
            ))}
          </InfiniteScroll>
        ) : (
          <EmptyList />
        )}
      </GenericPageContainer>
      {/* Modals */}
      <ModalReserveV2
        show={modalHandleReserve}
        size={'lg'}
        onHide={(isReserveUpdated) => {
          setModalHandleReserve(false);
          setSelectedReserve(null);
          if (isReserveUpdated) {
            mutate();
          }
        }}
        reserve_id={selectedReserve?.id}
      />
    </>
  );
}
